import { useState } from "react";
import { useScrollReveal } from "../../../hooks/useScrollReveal";
import { Product } from "../../../mocks/products";
import { supabase } from "../../../lib/supabase";
import ZaloLink from "../../../components/feature/ZaloLink";

interface Props {
  product: Product;
  formRef?: React.RefObject<HTMLDivElement | null>;
}

// ── Chính sách Thương mại (Left) ──────────────────────────────────────────────
const TradePolicyPanel = () => {
  const policies = [
    {
      icon: "ri-price-tag-3-line",
      title: "Chính sách Giá & Đối tác",
      color: "#004A99",
      items: [
        { icon: "ri-vip-crown-line", text: "Giá ưu đãi theo khối lượng: Chiết khấu tốt nhất cho đơn hàng lớn và hợp đồng cung ứng dài hạn." },
        { icon: "ri-settings-3-line", text: "Tùy chỉnh kỹ thuật: Giá linh hoạt theo quy cách, kích thước và đặc tính kỹ thuật riêng." },
        { icon: "ri-ship-line", text: "Logistics quốc tế: Hỗ trợ trọn gói Incoterms (FOB, CIF, DAP) giữa Hàn Quốc và Việt Nam." },
      ],
    },
    {
      icon: "ri-flask-line",
      title: "Hỗ trợ Kỹ thuật & Mẫu thử",
      color: "#00AEEF",
      items: [
        { icon: "ri-test-tube-line", text: "Cung cấp mẫu thử cho dự án tiềm năng để đánh giá độ tương thích." },
        { icon: "ri-shield-check-line", text: "Cam kết chất lượng thực tế đồng nhất với hồ sơ kỹ thuật." },
      ],
    },
    {
      icon: "ri-award-line",
      title: "Uy tín & Bảo chứng",
      color: "#F59E0B",
      items: [
        { icon: "ri-medal-line", text: "Chứng chỉ Quốc tế: Đầy đủ ISO/HACCP và báo cáo kiểm định (COA) kèm theo mỗi lô hàng." },
        { icon: "ri-customer-service-2-line", text: "Đội ngũ kỹ thuật Hàn Quốc hỗ trợ trực tuyến tức thì, xử lý mọi vấn đề vận hành." },
      ],
    },
  ];

  return (
    <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden flex flex-col h-full">
      <div className="bg-gradient-to-r from-[#001428] to-[#004A99] px-6 py-4">
        <div className="flex items-center gap-2.5 mb-0.5">
          <div className="w-8 h-8 flex items-center justify-center bg-white/20 rounded-xl flex-shrink-0">
            <i className="ri-file-list-3-line text-white text-base" />
          </div>
          <h3 className="text-base font-bold text-white">Chính sách Thương mại</h3>
        </div>
        <p className="text-white/70 text-xs ml-10">Cam kết minh bạch từ nhà sản xuất Hàn Quốc</p>
      </div>

      <div className="p-5 flex-1 space-y-5 overflow-auto">
        {policies.map((policy, pi) => (
          <div key={pi}>
            <div className="flex items-center gap-2 mb-3">
              <div className="w-7 h-7 flex items-center justify-center rounded-lg flex-shrink-0" style={{ backgroundColor: `${policy.color}15` }}>
                <i className={`${policy.icon} text-sm`} style={{ color: policy.color }} />
              </div>
              <h4 className="font-bold text-gray-900 text-xs">{policy.title}</h4>
            </div>
            <div className="space-y-2 pl-1">
              {policy.items.map((item, ii) => (
                <div key={ii} className="flex items-start gap-2.5 p-2.5 rounded-lg" style={{ backgroundColor: `${policy.color}06` }}>
                  <div className="w-5 h-5 flex items-center justify-center rounded-md flex-shrink-0 mt-0.5" style={{ backgroundColor: `${policy.color}15` }}>
                    <i className={`${item.icon} text-xs`} style={{ color: policy.color }} />
                  </div>
                  <p className="text-xs text-gray-600 leading-relaxed">{item.text}</p>
                </div>
              ))}
            </div>
            {pi < policies.length - 1 && <div className="mt-4 border-b border-gray-100" />}
          </div>
        ))}
      </div>
    </div>
  );
};

// ── Consultation Panel (Right) ──────────────────────────────────────────────
const ConsultCommentPanel = ({ product }: { product: Product }) => {
  const [consultSubmitted, setConsultSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [consultData, setConsultData] = useState({ name: "", email: "", phone: "", note: "" });

  const handleConsultSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!consultData.name || !consultData.email || !consultData.phone || !consultData.note) return;
    setSubmitting(true);
    try {
      await supabase.from("inquiries").insert({
        product_id: product.id,
        product_name: product.name,
        name: consultData.name,
        email: consultData.email,
        phone: consultData.phone,
        message: consultData.note,
        status: "new",
      });
      setConsultSubmitted(true);
    } catch {
      setConsultSubmitted(true);
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls = "w-full px-3 py-2 bg-[#F8FAFF] border border-gray-200 rounded-lg text-sm outline-none focus:border-[#004A99] transition-colors";

  return (
    <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden flex flex-col h-full">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#00AEEF] to-[#0099d4] px-6 py-4">
        <div className="flex items-center gap-2.5 mb-0.5">
          <div className="w-8 h-8 flex items-center justify-center bg-white/20 rounded-xl flex-shrink-0">
            <i className="ri-customer-service-2-line text-white text-base" />
          </div>
          <h3 className="text-base font-bold text-white">Liên hệ Tư vấn</h3>
        </div>
        <p className="text-white/70 text-xs ml-10">Chuyên gia song ngữ Hàn-Việt sẽ liên hệ trong 24 giờ</p>
      </div>

      <div className="p-5 flex-1 flex flex-col">
        {consultSubmitted ? (
          <div className="text-center py-6 flex-1 flex flex-col items-center justify-center">
            <div className="w-12 h-12 flex items-center justify-center bg-green-100 rounded-full mx-auto mb-3">
              <i className="ri-check-line text-green-500 text-xl" />
            </div>
            <h4 className="font-bold text-gray-900 mb-1 text-sm">Đã gửi thành công!</h4>
            <p className="text-gray-500 text-xs">Chúng tôi sẽ liên hệ trong vòng 24 giờ.</p>
            <button
              onClick={() => { setConsultSubmitted(false); setConsultData({ name: "", email: "", phone: "", note: "" }); }}
              className="mt-3 text-xs text-[#004A99] hover:underline cursor-pointer"
            >
              Gửi yêu cầu khác
            </button>
          </div>
        ) : (
          <form data-readdy-form onSubmit={handleConsultSubmit} className="flex flex-col gap-2.5">
            <div className="p-2.5 bg-[#F0F7FF] rounded-lg">
              <p className="text-[10px] text-gray-500 mb-0.5">Sản phẩm tư vấn</p>
              <p className="text-xs font-bold text-[#004A99] line-clamp-2">{product.name}</p>
            </div>

            <div className="grid grid-cols-2 gap-2.5">
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Họ và tên *</label>
                <input type="text" name="name" required value={consultData.name} onChange={(e) => setConsultData({ ...consultData, name: e.target.value })} placeholder="Nguyễn Văn A" className={inputCls} />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Email *</label>
                <input type="email" name="email" required value={consultData.email} onChange={(e) => setConsultData({ ...consultData, email: e.target.value })} placeholder="email@company.com" className={inputCls} />
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">Số điện thoại <span className="text-red-500">*</span></label>
              <input type="text" name="phone" required value={consultData.phone} onChange={(e) => setConsultData({ ...consultData, phone: e.target.value })} placeholder="+84 xxx xxx xxx" className={inputCls} />
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">Mô tả nhu cầu tư vấn của bạn <span className="text-red-500">*</span></label>
              <textarea
                name="note"
                rows={5}
                required
                value={consultData.note}
                onChange={(e) => { if (e.target.value.length <= 5000) setConsultData({ ...consultData, note: e.target.value }); }}
                placeholder="Mô tả nhu cầu tư vấn của bạn..."
                className={`${inputCls} resize-none`}
              />
              <p className="text-[10px] text-gray-400 text-right mt-0.5">{consultData.note.length}/5000</p>
            </div>

            {/* Submit + Zalo directly below textarea */}
            <button
              type="submit"
              disabled={submitting}
              className="w-full bg-[#004A99] hover:bg-[#003580] disabled:opacity-50 text-white py-2.5 rounded-xl font-semibold text-sm transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
            >
              {submitting ? <><i className="ri-loader-4-line animate-spin" />Đang gửi...</> : <><i className="ri-send-plane-line" />Đăng Ký Tư Vấn</>}
            </button>

            <p className="text-[11px] text-gray-400 text-center">Thông tin của bạn được bảo mật tuyệt đối</p>

            <div className="flex justify-center">
              <ZaloLink
                className="flex items-center gap-2 px-4 py-2 bg-[#0068FF]/8 hover:bg-[#0068FF]/15 border border-[#0068FF]/20 rounded-xl transition-all cursor-pointer group"
                tooltipPosition="bottom"
                title="Chat Zalo với Bizwell"
              >
                <div className="w-6 h-6 flex items-center justify-center bg-[#0068FF] rounded-lg flex-shrink-0 group-hover:scale-110 transition-transform">
                  <i className="ri-message-3-fill text-white text-xs" />
                </div>
                <span className="text-xs font-semibold text-[#0068FF]">Chat Zalo ngay</span>
              </ZaloLink>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

// ── Main Component ────────────────────────────────────────────────────────────
const ProductConsultation = ({ product, formRef }: Props) => {
  const { ref: sectionRef, isVisible } = useScrollReveal(0.05);

  return (
    <section id="inquiry" className="pt-8 pb-16 bg-[#F8FAFF]">
      <div className="max-w-[1100px] mx-auto px-8">
        <div
          ref={(el) => {
            (sectionRef as React.MutableRefObject<HTMLDivElement | null>).current = el;
            if (formRef) (formRef as React.MutableRefObject<HTMLDivElement | null>).current = el;
          }}
          className={`transition-all duration-700 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}
        >
          <div className="text-center mb-6">
            <p className="text-xs font-bold tracking-[0.25em] text-[#004A99] uppercase mb-2">CHÍNH SÁCH & TƯ VẤN</p>
            <h2 className="text-3xl font-bold text-gray-900 mb-2">Chính sách Thương mại & Liên hệ Tư vấn</h2>
            <p className="text-gray-500 max-w-xl mx-auto text-sm leading-relaxed">
              Tìm hiểu chính sách thương mại hoặc đăng ký tư vấn trực tiếp với chuyên gia song ngữ Hàn-Việt.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-6 items-stretch">
            <TradePolicyPanel />
            <ConsultCommentPanel product={product} />
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProductConsultation;
